import React, { useEffect, useState } from 'react';
import { auth, db } from '../firebase';
import { collection, query, where, orderBy, onSnapshot, doc, writeBatch, updateDoc, getDocs } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { getUserById } from '../services/userService';

const ListenerAlerts = ({ embedded = false }) => {
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [markingAllRead, setMarkingAllRead] = useState(false);

    // Check if user is a listener
    useEffect(() => {
        (async () => {
            if (!auth.currentUser) {
                if (!embedded) navigate('/login');
                return;
            }
            try {
                const userData = await getUserById(auth.currentUser.uid);
                setUser(userData);
                if (userData?.role !== 'listener') {
                    if (!embedded) navigate('/');
                }
            } catch (err) {
                console.error('Error fetching user:', err);
                navigate('/');
            }
        })();
        return () => {};
    }, [navigate, embedded]);

    // Subscribe to notifications in realtime
    useEffect(() => {
        if (!auth.currentUser || !user || user.role !== 'listener') {
            setLoading(false);
            return () => {};
        }

        setLoading(true);
        const notificationsRef = collection(db, 'notifications');
        const q = query(
            notificationsRef,
            where('userId', '==', auth.currentUser.uid),
            orderBy('createdAt', 'desc')
        );

        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                const notifs = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                // Sort by createdAt (fallback if orderBy doesn't work)
                notifs.sort((a, b) => {
                    const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : (a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0);
                    const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : (b.createdAt?.seconds ? b.createdAt.seconds * 1000 : 0);
                    return bTime - aTime;
                });
                setNotifications(notifs);
                setLoading(false);
            },
            (error) => {
                console.error('Error fetching notifications:', error);
                if (error.code === 'failed-precondition') {
                    // Keep alerts usable while the optional composite index is unavailable.
                    getDocs(query(
                        notificationsRef,
                        where('userId', '==', auth.currentUser.uid)
                    )).then((fallbackSnapshot) => {
                        const fallbackNotifications = fallbackSnapshot.docs.map((notificationDoc) => ({
                            id: notificationDoc.id,
                            ...notificationDoc.data(),
                        }));
                        fallbackNotifications.sort((a, b) => {
                            const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
                            const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
                            return bTime - aTime;
                        });
                        setNotifications(fallbackNotifications);
                    }).catch((fallbackError) => {
                        console.error('Fallback notifications query failed:', fallbackError);
                    });
                }
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, [user]);

    const handleMarkAllRead = async () => {
        if (!auth.currentUser || notifications.length === 0) return;

        setMarkingAllRead(true);
        try {
            const unreadNotifications = notifications.filter(n => !n.read);
            if (unreadNotifications.length === 0) {
                setMarkingAllRead(false);
                return;
            }

            const batch = writeBatch(db);
            unreadNotifications.forEach(notif => {
                const notifRef = doc(db, 'notifications', notif.id);
                batch.update(notifRef, { read: true });
            });

            await batch.commit();
            // Reset user's unreadNotifications counter
            try {
                await updateDoc(doc(db, 'users', auth.currentUser.uid), { unreadNotifications: 0 });
            } catch {}
        } catch (error) {
            console.error('Error marking all as read:', error);
            alert('Failed to mark all as read. Please try again.');
        } finally {
            setMarkingAllRead(false);
        }
    };

    const unreadCount = notifications.filter(n => !n.read).length;

    if (loading) {
        return (
            <div className="min-h-screen bg-[#0e0e0e] text-white flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                    <p>Loading alerts...</p>
                </div>
            </div>
        );
    }

    return (
        <div className={`${embedded ? '' : 'min-h-screen'} bg-[#0e0e0e] text-white`}>
            <div className="max-w-2xl mx-auto px-4 py-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        {!embedded && (
                            <button
                                onClick={() => navigate(-1)}
                                className="p-2 hover:bg-[#1a1a1a] rounded-lg transition"
                            >
                                ← Back
                            </button>
                        )}
                        <h1 className="text-2xl font-bold">Alerts</h1>
                        {unreadCount > 0 && (
                            <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full font-semibold">
                                {unreadCount} new
                            </span>
                        )}
                    </div>
                    {unreadCount > 0 && (
                        <button
                            onClick={handleMarkAllRead}
                            disabled={markingAllRead}
                            className="px-4 py-2 bg-[#1a1a1a] hover:bg-[#222] rounded-lg transition text-sm font-medium disabled:opacity-50"
                        >
                            {markingAllRead ? 'Marking...' : 'Mark all read'}
                        </button>
                    )}
                </div>

                {/* Notifications List */}
                {notifications.length === 0 ? (
                    <div className="text-center py-12 text-white/60">
                        <div className="text-4xl mb-4">🔔</div>
                        <p className="text-lg">No alerts yet</p>
                        <p className="text-sm mt-2">You'll see notifications here when someone follows you, sends you a message, or when artists you follow post new songs!</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {notifications.map((notif) => {
                            const isUnread = !notif.read;
                            const createdAt = notif.createdAt?.toDate ? notif.createdAt.toDate() : new Date(notif.createdAt?.seconds * 1000 || Date.now());
                            const timeAgo = getTimeAgo(createdAt);

                            return (
                                <div
                                    key={notif.id}
                                    className={`rounded-xl p-4 shadow-md transition-all ${
                                        isUnread
                                            ? 'bg-[#1a1a1a] border border-white/10 hover:bg-[#222] animate-pulse'
                                            : 'bg-[#1a1a1a]/50 hover:bg-[#1a1a1a] opacity-75'
                                    }`}
                                >
                                    <div className="flex items-start gap-3">
                                        {/* Icon based on type */}
                                        <div className="flex-shrink-0 mt-1">
                                            {notif.type === 'follow' && (
                                                <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                                                    <span className="text-xl">👤</span>
                                                </div>
                                            )}
                                            {notif.type === 'message' && (
                                                <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                                                    <span className="text-xl">💬</span>
                                                </div>
                                            )}
                                            {['tip', 'tip_confirmed', 'tip_pending', 'tip_failed'].includes(notif.type) && (
                                                <div
                                                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                                        notif.type === 'tip_failed'
                                                            ? 'bg-red-500/20'
                                                            : notif.type === 'tip_pending'
                                                                ? 'bg-yellow-500/20'
                                                                : 'bg-green-500/20'
                                                    }`}
                                                >
                                                    <span className="text-xl">
                                                        {notif.type === 'tip_failed'
                                                            ? '⚠️'
                                                            : notif.type === 'tip_pending'
                                                                ? '⏳'
                                                                : '💰'}
                                                    </span>
                                                </div>
                                            )}
                                            {notif.type === 'song_post' && (
                                                <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                                                    <span className="text-xl">🎵</span>
                                                </div>
                                            )}
                                            {!['follow', 'message', 'tip', 'tip_confirmed', 'tip_pending', 'tip_failed', 'song_post'].includes(notif.type) && (
                                                <div className="w-10 h-10 rounded-full bg-gray-500/20 flex items-center justify-center">
                                                    <span className="text-xl">🔔</span>
                                                </div>
                                            )}
                                        </div>

                                        {/* Content */}
                                        <div className="flex-1 min-w-0">
                                            <p className={`text-white ${isUnread ? 'font-medium' : 'font-normal'}`}>
                                                {notif.message}
                                            </p>
                                            <p className="text-xs text-white/50 mt-1">{timeAgo}</p>
                                        </div>

                                        {/* Unread indicator */}
                                        {isUnread && (
                                            <div className="flex-shrink-0">
                                                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

// Helper function to get time ago
function getTimeAgo(date) {
    const seconds = Math.floor((new Date() - date) / 1000);
    
    if (seconds < 60) return 'just now';
    
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    
    const weeks = Math.floor(days / 7);
    if (weeks < 4) return `${weeks}w ago`;
    
    const months = Math.floor(days / 30);
    return `${months}mo ago`;
}

export default ListenerAlerts;
